// file: C:/dartProBackend/dart-admin/src/trainingCoach/ClubDashboardPage.tsx
import * as React from "react";
import { useClub } from "../club/ClubContext";
import {
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Divider,
  Chip,
  Alert,
} from "@mui/material";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import { useDataProvider, useNotify, useRefresh, useStore } from "react-admin";

const CHART_COLORS = {
  numeric: "#1976d2",      // blau
  choices: "#9c27b0",      // lila
  x01Rounds: "#2e7d32",    // grün
  x01Darts: "#ef6c00",    // orange
  x01LastThrow: "#d32f2f", // rot

  barsSessions: "#90caf9",   // hellblau (gut auf dark)
  barsTemplates: "#ce93d8",  // hell-lila
};


type ScoreRanges = {
  "95_99": number;
  "100_119": number;
  "120_139": number;
  "140_167": number;
  "170": number;
  "171": number;
  "174": number;
  "177": number;
  "180": number;
};

type LeaderboardRow = { player_id: number; player_name: string; value: number; count: number };
type TemplateCountRow = { template_id: number; name: string; count: number };

type VisibleMember = {
  id: number;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  avatar_url?: string | null;
};

type DashboardResponse = {
  visible_members?: VisibleMember[];
  kpis: {
    date_from: string;
    date_to: string;
    sessions_total: number;
    active_players: number;
    end_avg_mean: number | null;
    end_avg_median: number | null;
    quote_mean: number | null;
    quote_median: number | null;

    // X01 (optional)
    x01_finish_rounds_mean?: number | null;
    x01_finish_rounds_median?: number | null;
    x01_finish_darts_mean?: number | null;
    x01_finish_darts_median?: number | null;
    x01_last_throw_max_mean?: number | null;
    x01_last_throw_max_median?: number | null;
  };
  series_sessions: { date: string; count: number }[];
  series_perf: {
    date: string;
    median_end_avg: number | null;
    median_quote: number | null;

    // X01 (optional)
    median_x01_rounds_to_finish?: number | null;
    median_x01_darts_to_finish?: number | null;
    median_x01_last_throw_max?: number | null;
  }[];

  // ✅ NEW (aktueller Backend-Stand)
  leaderboards?: {
    numeric: LeaderboardRow[];
    choices: LeaderboardRow[];
  };
  template_counts?: TemplateCountRow[];

  // ✅ LEGACY (falls noch vorhanden)
  top_numeric?: LeaderboardRow[];
  top_choices?: LeaderboardRow[];
  templates?: TemplateCountRow[];

  score_ranges?: ScoreRanges;
};

const SCORE_RANGES: Array<{ key: keyof ScoreRanges; label: string }> = [
  { key: "95_99", label: "95–99" },
  { key: "100_119", label: "100–119" },
  { key: "120_139", label: "120–139" },
  { key: "140_167", label: "140–167" },
  { key: "170", label: "170" },
  { key: "171", label: "171" },
  { key: "174", label: "174" },
  { key: "177", label: "177" },
  { key: "180", label: "180" },
];

const fmt = (v: number | null | undefined, digits = 2) =>
  v == null ? "–" : v.toFixed(digits);

function isoToday(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDays(iso: string, deltaDays: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + deltaDays);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function useDpFetch<T>(args: {
  enabled: boolean;
  deps: any[];
  fetcher: () => Promise<T>;
  onError?: (e: any) => void;
}) {
  const { enabled, deps, fetcher, onError } = args;
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [nonce, setNonce] = React.useState(0);

  const refetch = React.useCallback(() => setNonce((x) => x + 1), []);

  React.useEffect(() => {
    if (!enabled) return;

    let alive = true;
    setLoading(true);

    (async () => {
      try {
        const res = await fetcher();
        if (!alive) return;
        setData(res);
      } catch (e: any) {
        if (!alive) return;
        onError?.(e);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, nonce, ...deps]);

  return { data, loading, refetch };
}

export function ClubDashboardPage() {
  const { clubId } = useClub();
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // RA refresh hooks (je nach setup)
  const [raRefresh] = useStore("ra.refresh");
  const [legacyRefresh] = useStore("refresh");

  const [rangePreset, setRangePreset] = React.useState<"7" | "30" | "90" | "365" | "custom">("7");
  const [to, setTo] = React.useState<string>(isoToday());
  const [from, setFrom] = React.useState<string>(addDays(isoToday(), -6));
  const [playerId, setPlayerId] = React.useState<number | "all">("all");

  const [pdfLoading, setPdfLoading] = React.useState(false);

  React.useEffect(() => {
    if (rangePreset === "custom") return;
    const days = Number(rangePreset);
    setFrom(addDays(to, -(days - 1)));
  }, [rangePreset, to]);

  const qs = React.useMemo(() => {
    if (!clubId) return null;
    const p = new URLSearchParams();
    p.set("club_id", String(clubId));
    p.set("date_from", from);
    p.set("date_to", to);
    if (playerId !== "all") p.set("player_id", String(playerId)); // ✅ NEW
    return p.toString();
  }, [clubId, from, to, playerId]);

  const { data, loading, refetch } = useDpFetch<DashboardResponse>({
    enabled: !!clubId && !!qs,
    deps: [clubId, from, to, qs, raRefresh, legacyRefresh],
    fetcher: async () => {
      const res = await (dp as any).custom(`training/coach/dashboard?${qs!}`, { method: "GET" });
      return res.data as DashboardResponse;
    },
    onError: (e) => {
      const msg =
        e?.body?.detail ??
        e?.body?.message ??
        e?.message ??
        "Dashboard konnte nicht geladen werden.";
      notify(msg, { type: "warning" });
    },
  });

  // ✅ unified getters (NEW bevorzugt, fallback legacy)
  const topNumeric = data?.leaderboards?.numeric ?? data?.top_numeric ?? [];
  const topChoices = data?.leaderboards?.choices ?? data?.top_choices ?? [];
  const templates = data?.template_counts ?? data?.templates ?? [];

  const memberOptions = data?.visible_members ?? [];

  const memberLabel = (m: VisibleMember) =>
    (m.display_name && m.display_name.trim()) ||
    [m.first_name, m.last_name].filter(Boolean).join(" ").trim() ||
    m.email;

  const scoreRanges = data?.score_ranges;
  const highTotal = React.useMemo(() => {
    if (!scoreRanges) return null;
    return SCORE_RANGES.reduce((sum, r) => sum + (scoreRanges[r.key] ?? 0), 0);
  }, [scoreRanges]);

  const scoreRangeChartData = React.useMemo(() => {
    if (!scoreRanges) return [];
    return SCORE_RANGES.map((r) => ({
      range: r.label,
      count: scoreRanges[r.key] ?? 0,
    }));
  }, [scoreRanges]);

  const handleRefresh = () => {
    refetch();
    refresh();
  };


  const handleDownloadPdf = async () => {
    if (!clubId || !qs) return;
    setPdfLoading(true);
    try {
      // Wir versuchen zuerst über den DataProvider (nimmt i.d.R. Auth-Header mit)
      const res = await (dp as any).custom(`training/coach/report_pdf?${qs}`, {
        method: "GET",
        // Viele Custom-DataProvider reichen "meta.responseType" an den httpClient weiter
        responseType: "blob",
      });

      let blob: any = res?.blob ?? res?.data ?? res;

      // Fallbacks: ArrayBuffer → Blob
      if (blob && blob instanceof ArrayBuffer) {
        blob = new Blob([blob], { type: "application/pdf" });
      }

      // Fallback: base64-string → Blob
      if (typeof blob === "string") {
        const b64 = blob.startsWith("data:") ? blob.split(",")[1] : blob;
        const byteChars = atob(b64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: "application/pdf" });
      }

      if (!(blob instanceof Blob)) {
        throw new Error("Unerwartetes Response-Format (kein Blob).");
      }

      const filename = `dartmin_report_${from}_${to}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      const msg =
        e?.body?.detail ??
        e?.body?.message ??
        e?.message ??
        "PDF konnte nicht erzeugt werden.";
      notify(msg, { type: "warning" });
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2, width: "100%" }}>
      <Stack spacing={0.5}>
        <Typography variant="h5" fontWeight={900}>
          Vereins-Dashboard
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Überblick über Trainingsaktivität und Leistungswerte im ausgewählten Zeitraum.
        </Typography>
      </Stack>

      <Card sx={{ borderRadius: 1 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ alignItems: { xs: "stretch", sm: "center" }, flexWrap: "wrap" }}
          >
            <TextField
              select
              label="Zeitraum"
              size="small"
              value={rangePreset}
              onChange={(e) => setRangePreset(e.target.value as any)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="7">Letzte 7 Tage (Standard)</MenuItem>
              <MenuItem value="30">Letzte 30 Tage</MenuItem>
              <MenuItem value="90">Letzte 90 Tage</MenuItem>
              <MenuItem value="365">Letzte 12 Monate</MenuItem>
              <MenuItem value="custom">Benutzerdefiniert</MenuItem>
            </TextField>

            <TextField
              label="Von"
              type="date"
              size="small"
              value={from}
              onChange={(e) => {
                setRangePreset("custom");
                setFrom(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />

            <TextField
              label="Bis"
              type="date"
              size="small"
              value={to}
              onChange={(e) => {
                setRangePreset("custom");
                setTo(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
            <TextField
  select
  label="Spieler"
  size="small"
  value={playerId}
  onChange={(e) => setPlayerId(e.target.value === "all" ? "all" : Number(e.target.value))}
  sx={{ minWidth: 240 }}
>
  <MenuItem value="all">Alle sichtbaren Spieler</MenuItem>
  {memberOptions.map((m) => (
    <MenuItem key={m.id} value={m.id}>
      {memberLabel(m)}
    </MenuItem>
  ))}
</TextField>


            <Button variant="contained" onClick={handleRefresh} disabled={loading || !clubId}>
              Aktualisieren
            </Button>

            <Button
              variant="outlined"
              onClick={handleDownloadPdf}
              disabled={pdfLoading || loading || !clubId}
            >
              {pdfLoading ? "PDF…" : "PDF Report"}
            </Button>

            {data?.kpis ? (
              <Chip label={`${data.kpis.date_from} → ${data.kpis.date_to}`} variant="outlined" />
            ) : null}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Alert severity="info">
            <strong>So liest du die Zahlen:</strong>
            <br />
            • <strong>Median</strong> = der „typische“ Wert (die Mitte), robust gegen Ausreißer.
            <br />
            • <strong>Ø / Durchschnitt</strong> = normaler Mittelwert, Ausreißer wirken stärker.
            <br />
            <br />
            <strong>Je Spieltyp:</strong>
            <br />
            • <strong>End-Avg</strong> kommt nur aus <strong>numeric</strong>-Trainings (Punkte pro Aufnahme).
            <br />
            • <strong>Quote</strong> kommt nur aus <strong>choices</strong>-Trainings (Trefferquote in %).
            <br />
            • <strong>X01</strong> bezieht sich nur auf <strong>beendete</strong> X01-Spiele:
            <br />
            &nbsp;&nbsp;– <strong>Runden bis Finish</strong> = wie viele Aufnahmen bis Rest = 0.
            <br />
            &nbsp;&nbsp;– <strong>Darts bis Finish</strong> = wie viele Darts insgesamt bis Rest = 0.
            <br />
            &nbsp;&nbsp;– <strong>letzter Wurf (max)</strong> = höchster einzelner Dart im letzten Checkout-Wurf.
          </Alert>
        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ flexWrap: "wrap" }}>
        <Card sx={{ flex: 1, minWidth: 240, borderRadius: 1 }}>
          <CardContent>
            <Typography fontWeight={800}>Trainings</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Anzahl aller Trainings im Zeitraum
            </Typography>
            <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
              {data?.kpis.sessions_total ?? "–"}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 240, borderRadius: 1 }}>
          <CardContent>
            <Typography fontWeight={800}>Aktive Spieler</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Spieler mit mindestens 1 Training
            </Typography>
            <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
              {data?.kpis.active_players ?? "–"}
            </Typography>
          </CardContent>
        </Card>

        {data?.kpis?.x01_finish_rounds_median != null || data?.kpis?.x01_finish_rounds_mean != null ? (
          <Card sx={{ flex: 1, minWidth: 240, borderRadius: 1 }}>
            <CardContent>
              <Typography fontWeight={800}>X01: Runden bis Finish</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Median / Durchschnitt (nur beendete X01)
              </Typography>
              <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
                {fmt(data?.kpis.x01_finish_rounds_median, 0)} / {fmt(data?.kpis.x01_finish_rounds_mean, 1)}
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        {data?.kpis?.x01_finish_darts_median != null || data?.kpis?.x01_finish_darts_mean != null ? (
          <Card sx={{ flex: 1, minWidth: 240, borderRadius: 1 }}>
            <CardContent>
              <Typography fontWeight={800}>X01: Darts bis Finish</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Median / Durchschnitt (nur beendete X01)
              </Typography>
              <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
                {fmt(data?.kpis.x01_finish_darts_median, 0)} / {fmt(data?.kpis.x01_finish_darts_mean, 1)}
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        {data?.kpis?.x01_last_throw_max_median != null || data?.kpis?.x01_last_throw_max_mean != null ? (
          <Card sx={{ flex: 1, minWidth: 240, borderRadius: 1 }}>
            <CardContent>
              <Typography fontWeight={800}>X01: letzter Wurf</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Max-Wert im letzten Wurf (Median / Ø)
              </Typography>
              <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
                {fmt(data?.kpis.x01_last_throw_max_median, 0)} / {fmt(data?.kpis.x01_last_throw_max_mean, 1)}
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        <Card sx={{ flex: 1, minWidth: 240, borderRadius: 1 }}>
          <CardContent>
            <Typography fontWeight={800}>End-Avg (Verein)</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Median / Durchschnitt (nur numeric)
            </Typography>
            <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
              {fmt(data?.kpis.end_avg_median)} / {fmt(data?.kpis.end_avg_mean)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 240, borderRadius: 1 }}>
          <CardContent>
            <Typography fontWeight={800}>Quote (Verein)</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Median / Durchschnitt (nur choices)
            </Typography>
            <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
              {fmt(data?.kpis.quote_median, 1)}% / {fmt(data?.kpis.quote_mean, 1)}%
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 280, borderRadius: 1 }}>
          <CardContent>
            <Typography fontWeight={800}>High Scores (numeric)</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Wurfverteilung im Zeitraum (ohne Doppelzählung)
            </Typography>

            <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
              {highTotal ?? "–"}
            </Typography>

            {scoreRanges ? (
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", mt: 1 }}>
                {SCORE_RANGES.map((r) => (
                  <Chip
                    key={r.key}
                    size="small"
                    variant="outlined"
                    label={`${r.label}: ${scoreRanges[r.key] ?? 0}`}
                  />
                ))}
              </Stack>
            ) : null}
          </CardContent>
        </Card>
      </Stack>

      <Divider />

      {scoreRanges ? (
        <Card sx={{ borderRadius: 1 }}>
          <CardContent>
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              <Typography fontWeight={900}>High-Score Verteilung (numeric)</Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                Anzahl Würfe je Bereich im Zeitraum.
              </Typography>
            </Stack>

            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={scoreRangeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" name="Würfe" fill={CHART_COLORS.x01Rounds} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card sx={{ borderRadius: 1 }}>
        <CardContent>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography fontWeight={900}>Trainings pro Tag</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Anzahl Trainings pro Tag.
            </Typography>
          </Stack>

          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data?.series_sessions ?? []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name="Trainings" fill={CHART_COLORS.barsSessions}  radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 1 }}>
        <CardContent>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography fontWeight={900}>Vereins-Performance (Median)</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Median je Tag (robust gegen Ausreißer).
            </Typography>
          </Stack>

          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={data?.series_perf ?? []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
  type="monotone"
  dataKey="median_end_avg"
  name="Median End-Avg (numeric)"
  stroke={CHART_COLORS.numeric}
  strokeWidth={2}
  dot={false}
/>

<Line
  type="monotone"
  dataKey="median_quote"
  name="Median Quote % (choices)"
  stroke={CHART_COLORS.choices}
  strokeWidth={2}
  dot={false}
/>

<Line
  type="monotone"
  dataKey="median_x01_rounds_to_finish"
  name="Median Runden bis Finish (X01)"
  stroke={CHART_COLORS.x01Rounds}
  strokeWidth={2}
  dot={false}
/>

<Line
  type="monotone"
  dataKey="median_x01_darts_to_finish"
  name="Median Darts bis Finish (X01)"
  stroke={CHART_COLORS.x01Darts}
  strokeWidth={2}
  dot={false}
/>

<Line
  type="monotone"
  dataKey="median_x01_last_throw_max"
  name="Median letzter Wurf max (X01)"
  stroke={CHART_COLORS.x01LastThrow}
  strokeWidth={2}
  dot={false}
/>

              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Card sx={{ flex: 1, borderRadius: 1 }}>
          <CardContent>
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              <Typography fontWeight={900}>Top 10 Spieler (End-Avg)</Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                nur numeric.
              </Typography>
            </Stack>

            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={topNumeric} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="player_name" width={160} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Ø End-Avg" fill={CHART_COLORS.barsSessions}  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, borderRadius: 1 }}>
          <CardContent>
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              <Typography fontWeight={900}>Top 10 Spieler (Quote)</Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                nur choices.
              </Typography>
            </Stack>

            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={topChoices} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="player_name" width={160} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Ø Quote %" fill={CHART_COLORS.barsSessions}  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ borderRadius: 1 }}>
        <CardContent>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography fontWeight={900}>Beliebteste Trainingsspiele</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Häufigkeit im Zeitraum.
            </Typography>
          </Stack>

          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={templates} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={220} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name="Trainings" fill={CHART_COLORS.barsTemplates} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </Stack>
  );
}
